/**
 * HTTP Error Messages Dictionary
 * Centralized error messages for different HTTP status codes and error types
 */

const ERROR_MESSAGES = {
  // Rate limiting
  429: 'Service is temporarily busy due to high traffic. Please wait a moment and try again.',

  // Client errors
  400: 'Invalid request. Please try again.',
  401: 'Authentication required. Please refresh the page.',
  403: 'You do not have permission to access this content.',
  404: 'Content not found.',

  // Server errors
  500: 'Server error. Please try again in a few moments.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
  504: 'Request timeout. Please try again.',

  // Network errors
  NETWORK_ERROR:
    'Oops, there is something wrong with your internet connection. 😢 Reload and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please check your connection and try again.',
};

/**
 * Get user-friendly error message based on HTTP status code or error type
 * @param {Error|number|string} error - Error object, HTTP status code, or error type
 * @param {string} defaultMessage - Default error message if status/type is unknown
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(
  error,
  defaultMessage = 'An error occurred. Please try again later.',
) {
  // If error is a number (status code), look it up directly
  if (typeof error === 'number') {
    return ERROR_MESSAGES[error] || defaultMessage;
  }

  // If error is a string (error type), look it up directly
  if (typeof error === 'string') {
    return ERROR_MESSAGES[error] || defaultMessage;
  }

  // If error is an Error object, extract status code
  if (error && typeof error === 'object') {
    const status = error?.response?.status;

    // Check for specific status codes
    if (status && ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }

    // Check for network errors
    if (
      error?.message?.includes('network') ||
      error?.code === 'NETWORK_ERROR' ||
      error?.code === 'ERR_NETWORK'
    ) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }

    // Check for timeout errors
    if (
      error?.message?.includes('timeout') ||
      error?.code === 'TIMEOUT_ERROR' ||
      error?.code === 'ECONNABORTED'
    ) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }

    // Handle 5xx server errors generically if specific code not found
    if (status >= 500) {
      return ERROR_MESSAGES[500];
    }

    // Handle 4xx client errors generically if specific code not found
    if (status >= 400) {
      return ERROR_MESSAGES[400];
    }
  }

  return defaultMessage;
}

/**
 * Get API error message (for non-Storyblok APIs)
 * Wrapper around getErrorMessage with API-specific defaults
 * @param {Error} error - Error object from API request
 * @param {string} defaultMessage - Default error message if status is unknown
 * @returns {string} User-friendly error message
 */
export function getApiErrorMessage(
  error,
  defaultMessage = 'An error occurred. Please try again later.',
) {
  return getErrorMessage(error, defaultMessage);
}

export default {
  ERROR_MESSAGES,
  getErrorMessage,
  getApiErrorMessage,
};
